import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { Icon } from '../Icon';
import { colors, spacing, borderRadius, fontWeights } from '../../theme';

interface M07FlowerViewProps {
  customerName: string;
  customerAge: string;
  totalPremiumNum: number;
  formatNumber: (num: number) => string;
  r1Package: 'silver' | 'gold' | 'platinum';
  r2LimitCuoi: number;
  r3DailyAllowance: number;
  r4PhysicalLimit: number;
  r5Mode: 'attached' | 'main' | 'complimentary';
  r5SumAssured: number;
  selectedRiders: string[];
}

export const M07FlowerView: React.FC<M07FlowerViewProps> = ({
  customerName,
  customerAge,
  totalPremiumNum,
  formatNumber,
  r1Package,
  r2LimitCuoi,
  r3DailyAllowance,
  r4PhysicalLimit,
  r5Mode,
  r5SumAssured,
  selectedRiders,
}) => {
  const [selectedPetal, setSelectedPetal] = useState<string>('R1');
  const dailySavings = Math.round(totalPremiumNum / 365);

  const petals = [
    { id: 'R1', name: 'Sống Khỏe Mỗi Ngày', shortName: 'Sống Khỏe', color: '#E11D48', icon: 'medical_services', emoji: '🩺', code: 'R1' },
    { id: 'R2', name: 'Lá Chắn Xanh', shortName: 'Lá Chắn Xanh', color: '#D97706', icon: 'coronavirus', emoji: '🛡️', code: 'R2' },
    { id: 'R3', name: 'Dự Phòng Xanh', shortName: 'Dự Phòng Xanh', color: '#00A651', icon: 'savings', emoji: '☂️', code: 'R3' },
    { id: 'R4', name: 'Hộ Vệ Xanh', shortName: 'Hộ Vệ Xanh', color: '#2563EB', icon: 'local_hospital', emoji: '🏥', code: 'R4' },
    { id: 'R5', name: 'Bảo Thân Xanh', shortName: 'Bảo Thân Xanh', color: '#7C3AED', icon: 'shield', emoji: '❤️', code: 'R5' },
  ];

  const activePetalData = petals.find((p) => p.id === selectedPetal) || petals[0];
  const isRiderSelected = (riderId: string): boolean => selectedRiders.includes(riderId);

  return (
    <View style={styles.flowerContainer}>
      <View style={styles.flowerHeaderStats}>
        <View style={styles.flowerStatBox}>
          <Text style={styles.flowerStatLabel}>Người được BH chính</Text>
          <Text style={styles.flowerStatValue} numberOfLines={1}>
            {customerName}
          </Text>
          <Text style={styles.flowerStatSub}>{customerAge} tuổi</Text>
        </View>

        <View style={styles.flowerStatBox}>
          <Text style={styles.flowerStatLabel}>Phí bảo hiểm định kỳ</Text>
          <Text style={styles.flowerStatValue} numberOfLines={1}>
            {formatNumber(totalPremiumNum)} đ
          </Text>
          <Text style={styles.flowerStatSub}>Hàng năm</Text>
        </View>

        <View style={styles.flowerStatBox}>
          <Text style={styles.flowerStatLabel}>Tiết kiệm mỗi ngày</Text>
          <Text style={[styles.flowerStatValue, { color: colors.secondary }]} numberOfLines={1}>
            ~{formatNumber(dailySavings)} đ/ngày
          </Text>
          <Text style={styles.flowerStatSub}>Chi phí tích lũy bảo vệ</Text>
        </View>
      </View>

      <View style={styles.flowerCard}>
        <Text style={styles.flowerCardTitle}>🌸 Bông hoa giải pháp tài chính</Text>
        <Text style={styles.flowerCardSub}>
          Bấm chọn từng cánh hoa hoặc các nút bên dưới để xem quyền lợi chi tiết
        </Text>

        {Platform.OS === 'web' ? (
          <View style={styles.flowerSvgWrapper}>
            <svg width="340" height="340" viewBox="0 0 340 340">
              <defs>
                <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                  <feDropShadow dx="0" dy="4" stdDeviation="4" floodOpacity="0.15" />
                </filter>
              </defs>
              <g transform="translate(170, 170)">
                {petals.map((petal, index) => {
                  const angle = (index * 72 - 90) * (Math.PI / 180);
                  const isSelected = selectedPetal === petal.id;
                  const scale = isSelected ? 1.08 : 1.0;
                  const distance = isSelected ? 16 : 8;
                  const cx = Math.cos(angle) * distance;
                  const cy = Math.sin(angle) * distance;
                  const rotateDeg = index * 72;

                  return (
                    <g
                      key={petal.id}
                      transform={`translate(${cx}, ${cy}) rotate(${rotateDeg}) scale(${scale})`}
                      style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
                      onClick={() => setSelectedPetal(petal.id)}
                    >
                      <path
                        d="M 0 0 C -52 -55, -45 -120, 0 -138 C 45 -120, 52 -55, 0 0 Z"
                        fill={petal.color}
                        opacity={isSelected ? 0.95 : 0.78}
                        stroke="#ffffff"
                        strokeWidth="3"
                        filter={isSelected ? 'url(#shadow)' : undefined}
                      />
                      <circle cx="0" cy="-75" r="18" fill="#ffffff" opacity="0.95" filter="url(#shadow)" />
                      <text x="0" y="-69" textAnchor="middle" fontSize="15">
                        {petal.emoji}
                      </text>
                      <text
                        x="0"
                        y="-112"
                        textAnchor="middle"
                        fill="#ffffff"
                        fontSize="10"
                        fontWeight="bold"
                        letterSpacing="0.3"
                      >
                        {petal.shortName}
                      </text>
                    </g>
                  );
                })}

                <circle cx="0" cy="0" r="42" fill="#ffffff" filter="url(#shadow)" stroke="#00A651" strokeWidth="3" />
                <text x="0" y="-8" textAnchor="middle" fill="#002244" fontSize="12" fontWeight="bold">
                  MANULIFE
                </text>
                <text x="0" y="8" textAnchor="middle" fill="#00A651" fontSize="10" fontWeight="700">
                  GIẢI PHÁP
                </text>
                <text x="0" y="22" textAnchor="middle" fill="#64748b" fontSize="9" fontWeight="500">
                  TOÀN DIỆN
                </text>
              </g>
            </svg>
          </View>
        ) : (
          <View style={styles.flowerFallback}>
            <Text style={styles.flowerTitle}>[Sơ đồ Bông Hoa Giải Pháp]</Text>
          </View>
        )}

        <View style={styles.petalsSelectorRow}>
          {petals.map((petal) => {
            const isSelected = selectedPetal === petal.id;
            const isAttached = isRiderSelected(petal.id);
            return (
              <TouchableOpacity
                key={petal.id}
                style={[
                  styles.petalPill,
                  { borderColor: petal.color },
                  isSelected && { backgroundColor: petal.color },
                ]}
                onPress={() => setSelectedPetal(petal.id)}
              >
                <Text style={[styles.petalPillText, { color: isSelected ? '#ffffff' : petal.color }]}>
                  {petal.emoji} {petal.name} {isAttached ? '✓' : ''}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>

        {(() => {
          const petal = activePetalData;
          const isSelected = isRiderSelected(petal.id);
          let displayBullets: string[] = [];

          if (petal.id === 'R1') {
            const r1LimitText =
              r1Package === 'silver'
                ? '125.000.000 đ'
                : r1Package === 'gold'
                ? '250.000.000 đ'
                : '500.000.000 đ';
            displayBullets = [
              `Hạn mức bảo vệ y tế nội trú lên đến ${r1LimitText}/năm.`,
              'Chi trả 100% chi phí điều trị nội trú, phẫu thuật thực tế tại bệnh viện.',
              'Bảo lãnh viện phí 24/7 tại hệ thống hơn 300 bệnh viện chất lượng cao trong nước.',
            ];
          } else if (petal.id === 'R2') {
            displayBullets = [
              `Bảo vệ tài chính trước 134 Bệnh lý nghiêm trọng với hạn mức ${formatNumber(r2LimitCuoi)} đ.`,
              'Chi trả tối đa lên đến 325% Số tiền bảo hiểm qua nhiều giai đoạn.',
              'Hỗ trợ tài chính khẩn cấp giúp khách hàng an tâm tiếp cận các liệu trình điều trị tiên tiến.',
            ];
          } else if (petal.id === 'R3') {
            displayBullets = [
              `Trợ cấp tiền mặt ${formatNumber(r3DailyAllowance)} đ/ngày nằm viện thực tế để bù đắp thu nhập.`,
              'Nhân đôi mức trợ cấp khi nằm viện điều trị tại Khoa chăm sóc đặc biệt (ICU).',
              'Thủ tục thanh toán đơn giản, nhanh chóng qua ứng dụng di động.',
            ];
          } else if (petal.id === 'R4') {
            displayBullets = [
              `Chi trả tối đa lên tới ${formatNumber(r4PhysicalLimit)} đ đối với thương tật do tai nạn.`,
              'Bảo vệ 24/7 phạm vi toàn cầu bao gồm tai nạn giao thông, sinh hoạt, thể thao, lao động.',
              'Hỗ trợ chi phí nằm viện nội trú đặc biệt do tai nạn.',
            ];
          } else if (petal.id === 'R5') {
            const finalR5SumAssured = r5Mode === 'attached' ? r5SumAssured : 750_000_000;
            displayBullets = [
              `Quyền lợi bảo hiểm sinh mạng (Tử vong & Thương tật) lên đến ${formatNumber(
                finalR5SumAssured
              )} đ.`,
              'Đảm bảo tương lai học vấn của con cái và gia đình không bị ảnh hưởng đứt quãng trước các biến cố.',
              'Đảm bảo hợp đồng bảo hiểm chính tiếp tục duy trì hiệu lực.',
            ];
          }

          return (
            <View style={[styles.petalDetailCard, { borderLeftColor: petal.color }]}>
              <View style={styles.petalDetailHeader}>
                <Icon name={petal.icon as any} size={20} color={petal.color} />
                <Text style={[styles.petalDetailTitle, { color: petal.color }]}>{petal.name}</Text>
                <View
                  style={[
                    styles.statusBadge,
                    { backgroundColor: isSelected ? 'rgba(0, 166, 81, 0.08)' : 'rgba(239, 68, 68, 0.08)' },
                  ]}
                >
                  <Text style={[styles.statusBadgeText, { color: isSelected ? '#00A651' : '#EF4444' }]}>
                    {isSelected ? 'Đang tham gia' : 'Chưa tham gia'}
                  </Text>
                </View>
              </View>
              <View style={styles.petalDetailBullets}>
                {displayBullets.map((bullet, idx) => (
                  <View key={idx} style={styles.bulletRow}>
                    <Icon name="check" size={14} color={isSelected ? '#00A651' : '#cbd5e1'} />
                    <Text style={[styles.bulletText, !isSelected && { color: colors.onSurfaceVariant }]}>
                      {bullet}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          );
        })()}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  flowerContainer: {
    gap: spacing[4],
  },
  flowerHeaderStats: {
    flexDirection: 'row',
    gap: spacing[3],
    flexWrap: 'wrap',
  },
  flowerStatBox: {
    flex: 1,
    minWidth: 150,
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: 14,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.03,
    shadowRadius: 2,
    elevation: 1,
  },
  flowerStatLabel: {
    fontSize: 11,
    color: '#64748b',
    fontWeight: fontWeights.medium,
    marginBottom: spacing[1],
  },
  flowerStatValue: {
    fontSize: 16,
    fontWeight: fontWeights.bold,
    color: colors.primary,
    marginBottom: 2,
  },
  flowerStatSub: {
    fontSize: 11,
    color: '#94a3b8',
  },
  flowerCard: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[5],
    borderWidth: 1,
    borderColor: '#e2e8f0',
    alignItems: 'center',
    gap: spacing[3],
  },
  flowerCardTitle: {
    fontSize: 18,
    fontWeight: fontWeights.bold,
    color: '#1e293b',
  },
  flowerCardSub: {
    fontSize: 13,
    color: '#64748b',
    textAlign: 'center',
  },
  flowerSvgWrapper: {
    marginVertical: spacing[2],
    alignItems: 'center',
    justifyContent: 'center',
  },
  flowerFallback: {
    height: 200,
    justifyContent: 'center',
    alignItems: 'center',
  },
  flowerTitle: {
    fontSize: 16,
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  petalsSelectorRow: {
    flexDirection: 'row',
    gap: spacing[2],
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginVertical: spacing[1],
  },
  petalPill: {
    paddingHorizontal: spacing[3],
    paddingVertical: spacing[1.5],
    borderRadius: borderRadius.full,
    borderWidth: 1.5,
    backgroundColor: '#ffffff',
  },
  petalPillText: {
    fontSize: 12,
    fontWeight: fontWeights.bold,
  },
  petalDetailCard: {
    width: '100%',
    backgroundColor: '#f8fafc',
    borderRadius: borderRadius.sm,
    padding: spacing[4],
    borderLeftWidth: 4,
    gap: spacing[3],
  },
  petalDetailHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
  },
  petalDetailTitle: {
    flex: 1,
    fontSize: 15,
    fontWeight: fontWeights.bold,
  },
  statusBadge: {
    paddingHorizontal: spacing[2.5],
    paddingVertical: spacing[1],
    borderRadius: borderRadius.full,
  },
  statusBadgeText: {
    fontSize: 11,
    fontWeight: fontWeights.bold,
  },
  petalDetailBullets: {
    gap: spacing[2],
  },
  bulletRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing[2],
  },
  bulletText: {
    flex: 1,
    fontSize: 13,
    color: colors.onSurface,
    lineHeight: 18,
  },
});
